import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {CountriesListService} from '../../../../services/countries-list.service';
import {UserService} from '../../../../services/user.service';
import {UtilService} from '../../../../util/util.service';
import {asciiValidator} from '../../../../validators/ascii-validator.directive';
import {AppConfigService} from '../../../../services/app.config.service';
import {UserObject} from '@iana-rzms/bff-sdk';
import {CommonDatePipe} from '../../../../pipes/common-date.pipe';
import {requiredFieldValidator} from '../../../../validators/required-field-validator.directive';
import {whitelistCharacterValidator} from '../../../../validators/valid-character-validator.directive';
import {emailValidator} from '../../../../validators/email-validator.directive';

@Component({
    selector: 'app-user-profile-edit',
    templateUrl: './user-profile-edit.component.html',
    styleUrls: ['./user-profile-edit.component.scss'],
    standalone: false
})
export class UserProfileEditComponent implements OnInit {
  @Input()
  user: UserObject;

  @Input()
  signUpMode: boolean;

  editUserProfileForm: FormGroup;

  error: any;

  commonDatePipe: CommonDatePipe;

  showOtherCountryElement: boolean;
  showOtherCountryElementPlaceOfResidence: boolean;

  roles = [];
  todayDate: string;
  minDate: string;
  countries: any[] = [];

  @Output()
  editProfileCompleted=new EventEmitter<boolean>();

  get loginName(): FormControl { return this.editUserProfileForm.get('loginName') as FormControl; }
  get firstName(): FormControl { return this.editUserProfileForm.get('firstName') as FormControl; }
  get lastName(): FormControl { return this.editUserProfileForm.get('lastName') as FormControl; }
  get phone(): FormControl { return this.editUserProfileForm.get('phone') as FormControl; }
  get mobile(): FormControl { return this.editUserProfileForm.get('mobile') as FormControl; }
  get fax(): FormControl { return this.editUserProfileForm.get('fax') as FormControl; }
  get otherCountryPlaceOfResidence(): FormControl { return this.editUserProfileForm.get('otherCountryPlaceOfResidence') as FormControl; }
  get organizationName(): FormControl { return this.editUserProfileForm.get('organizationName') as FormControl; }
  get streetAddress(): FormControl { return this.editUserProfileForm.get('streetAddress') as FormControl; }
  get city(): FormControl { return this.editUserProfileForm.get('city') as FormControl; }
  get otherCountry(): FormControl { return this.editUserProfileForm.get('otherCountry') as FormControl; }
  get state(): FormControl { return this.editUserProfileForm.get('state') as FormControl; }
  get postalCode(): FormControl { return this.editUserProfileForm.get('postalCode') as FormControl; }

  constructor(private formBuilder: FormBuilder, private countriesListService: CountriesListService, private util: UtilService, private appConfigService: AppConfigService, private userService: UserService) { }

  ngOnInit(): void {
    this.countriesListService.load();
    this.commonDatePipe = new CommonDatePipe(this.util);
    if (this.isTldManagerProfile()) {
      this.roles = ['TLD_MANAGER'];
    } else{
      this.roles = ['STAFF', 'STAFF_ADMIN'];
    }
    this.todayDate = this.commonDatePipe.transform(new Date().toISOString(), true);
    this.minDate = '1920-01-01';
    this.countries = this.countriesListService.countriesList;
    this.createForm();

  }

  private createForm() {
    console.log(this.user);
    this.error = null;
    if (!this.user.address) {
      this.user.address = {};
    }
    this.editUserProfileForm = this.formBuilder.group({
      loginName: new FormControl(this.user.loginName, [requiredFieldValidator, whitelistCharacterValidator]),
      firstName: new FormControl(this.user.firstName, [requiredFieldValidator, whitelistCharacterValidator]),
      lastName: new FormControl(this.user.lastName, [requiredFieldValidator, whitelistCharacterValidator]),
      email: new FormControl(this.user.email, [requiredFieldValidator, emailValidator]),
      phone: [this.user.phone, [whitelistCharacterValidator]],
      mobile: [this.user.mobile, [whitelistCharacterValidator]],
      fax: [this.user.fax, [whitelistCharacterValidator]],
      residenceCountryCode: [this.countriesListService.getCountry(false, {
        countryCode: this.user.residenceCountryCode,
        countryName: this.user.residenceCountryName
      }), [requiredFieldValidator]],
      otherCountryPlaceOfResidence: [this.countriesListService.getCountry(true, {
        countryCode: this.user.residenceCountryCode,
        countryName: this.user.residenceCountryName
      }), [asciiValidator, whitelistCharacterValidator]],
      organizationName: [this.user.organizationName, [whitelistCharacterValidator]],
      streetAddress: [this.user.address.streetAddress, [whitelistCharacterValidator]],
      city: [this.user.address.city, [whitelistCharacterValidator]],
      state: [this.user.address.state, [whitelistCharacterValidator]],
      countryName: [this.countriesListService.getCountry(false, this.user.address), [requiredFieldValidator]],
      otherCountry: [this.countriesListService.getCountry(true, this.user.address), [whitelistCharacterValidator]],
      postalCode: [this.user.address.postalCode, [whitelistCharacterValidator]],
      role: new FormControl(this.user.roles)
    });

    this.showOtherElementPlaceOfResidence();
    this.showOtherElement();

    this.editUserProfileForm.get('loginName').disable();
    if (this.appConfigService.isTldManagerInterface() ) {
      this.editUserProfileForm.get('email').disable();
      if(!this.signUpMode){
        this.editUserProfileForm.get('firstName').disable();
        this.editUserProfileForm.get('lastName').disable();
      }
    }
  }

  onSubmit() {
    const formValue = this.editUserProfileForm.getRawValue();

    this.error = null;
    let addresVO = this.countriesListService.buildAddressFromForm(formValue);

    if (!addresVO.streetAddress && !addresVO.city && !addresVO.state && !addresVO.countryName && !addresVO.postalCode) {
      addresVO = null;
    }

    let placeOfResidenceCountryCode: string = this.countriesListService.getCountryCode(formValue, 'residenceCountryCode');
    let residenceCountryName: string = this.countriesListService.getCountryName(formValue, 'residenceCountryCode', 'otherCountryPlaceOfResidence');

    let firstName = formValue.firstName;
    let lastName = formValue.lastName;
    let email = formValue.email;

    let user: UserObject = {
      id: this.user.id,
      loginName: this.user.loginName,
      firstName: firstName,
      lastName: lastName,
      email: email,
      dateOfBirth: this.user.dateOfBirth,
      phone: this.util.returnNullIfEmpty(formValue.phone),
      mobile: this.util.returnNullIfEmpty(formValue.mobile),
      fax: this.util.returnNullIfEmpty(formValue.fax),
      residenceCountryCode: placeOfResidenceCountryCode,
      residenceCountryName: residenceCountryName,
      organizationName: this.util.returnNullIfEmpty(formValue.organizationName),
      address: addresVO,
      roles: formValue.role,
      totpEnabled: this.user.totpEnabled
    };
    
    // Remove null properties to comply with OpenAPI schema (Optional & Not Nullable)
    user = this.util.removeNullProperties(user) as UserObject;

    this.userService.createOrEditUser(user, this.signUpMode.valueOf()).subscribe(user => {
      this.user = user as UserObject;
    }, error => {
      this.error = error;
    }, () => {
        this.editProfileCompleted.emit(true);
    });
  }

  showOtherElement() {
    this.showOtherCountryElement = this.editUserProfileForm.value.countryName === 'other';
  }

  showOtherElementPlaceOfResidence() {
    this.showOtherCountryElementPlaceOfResidence = this.editUserProfileForm.value.residenceCountryCode === 'other';
  }


  cancel() {
    this.editProfileCompleted.emit(false);
  }

  isTldManagerInterface() {
    return this.appConfigService.isTldManagerInterface();
  }

  isTldManagerProfile() {
    return this.appConfigService.isTldManagerInterface() || (this.appConfigService.isStaffInterface() && this.user.roles.filter(role => role === 'TLD_MANAGER').length > 0);
  }
}
